import { module as Module } from 'modujs';
import gsap from 'gsap';
import { clamp, roundNumber } from '../utils/maths';
import { createFocusTrap } from 'focus-trap';
import { $hasPlayerOpen } from '../stores/state';
import { delegateEvent } from '../utils/events';

export default class Player extends Module {
    static CLASS = {
        PLAYING: 'is-playing',
        MUTED: 'is-muted',
        ACTIVE: 'is-active',
        GLOBAL_ACTIVE: 'has-player-open'
    };

    static SELECTOR = '[data-player-open]';

    static LERP = 0.1;

    constructor(m) {
        super(m);

        this.onTogglePlay = this.onTogglePlay.bind(this);
        this.onToggleSound = this.onToggleSound.bind(this);
        this.onVideoEnded = this.onVideoEnded.bind(this);
        this.onVideoClick = this.onVideoClick.bind(this);
        this.onTimelineInput = this.onTimelineInput.bind(this);
        this.onTimelineChange = this.onTimelineChange.bind(this);
        this.updateRaf = this.updateRaf.bind(this);
        this.onPlayerOpenChange = this.onPlayerOpenChange.bind(this);
        this.onOpen = this.onOpen.bind(this);
        this.onClose = this.onClose.bind(this);
        this.onActivate = this.onActivate.bind(this);
        this.onPostActivate = this.onPostActivate.bind(this);
        this.onDeactivate = this.onDeactivate.bind(this);
        this.onPostDeactivate = this.onPostDeactivate.bind(this);

        // UI
        this.$video = this.$('video')[0];
        this.$toggle = this.$('toggle')[0];
        this.$sound = this.$('sound')[0];
        this.$input = this.$('input')[0];
        this.$close = this.$('close')[0];

        // Flags
        this.isMuted = false;
        this.isPlaying = false;

        // Data
        this.playerId = this.el.dataset.playerId;

        this.progress = {
            default: 0,
            smooth: 0
        };

        this.lerp = 1;

        // Raf
        this.isRafPlaying = false;
        this.rafIncrement = 0;

        // Focus trap
        this.focusTrap = createFocusTrap(this.el, {
            onActivate: this.onActivate,
            onPostActivate: this.onPostActivate,
            onDeactivate: this.onDeactivate,
            onPostDeactivate: this.onPostDeactivate,
            clickOutsideDeactivates: true
        });
    }

    // //////////
    // Lifecycle
    // //////////

    init() {
        this.bindEvents();

        if (window.matchMedia(`(hover: none)`).matches) {
            this.$video.setAttribute('controls', 'controls');
        }
    }

    destroy() {
        this.unbindEvents();
        super.destroy();
    }

    // //////////
    // Events
    // //////////

    bindEvents() {
        this.unbindOpenEvent = delegateEvent(document.body, 'click', Player.SELECTOR, this.onOpen);
        this.unbindPlayerOpenListener = $hasPlayerOpen.listen(this.onPlayerOpenChange);
        this.$close?.addEventListener('click', this.onClose);

        if (window.matchMedia(`(hover: hover)`).matches) {
            this.$toggle.addEventListener('click', this.onTogglePlay);
            this.$sound.addEventListener('click', this.onToggleSound);
            this.$video.addEventListener('click', this.onVideoClick);
            this.$video.addEventListener('ended', this.onVideoEnded);
            this.$input.addEventListener('input', this.onTimelineInput);
            this.$input.addEventListener('change', this.onTimelineChange);
        }
    }

    unbindEvents() {
        this.unbindOpenEvent.abort?.();
        this.unbindPlayerOpenListener?.();
        this.$close?.addEventListener('click', this.onClose);

        if (window.matchMedia(`(hover: hover)`).matches) {
            this.$toggle.removeEventListener('click', this.onTogglePlay);
            this.$sound.removeEventListener('click', this.onToggleSound);
            this.$video.removeEventListener('click', this.onVideoClick);
            this.$video.removeEventListener('ended', this.onVideoEnded);
            this.$input.removeEventListener('input', this.onTimelineInput);
            this.$input.removeEventListener('change', this.onTimelineChange);
        }
    }

    // //////////
    // Callbacks
    // //////////

    onTimelineInput(e) {
        // Pause video
        this.$video.pause();
        this.lerp = Player.LERP;

        const { value } = e.target;
        this.$video.currentTime = this.$video.duration * parseFloat(value);

        this.updateProgress();
        this.playRaf();
    }

    onTimelineChange(e) {
        this.isPlaying && this.$video.play();
    }

    onTogglePlay(e) {
        if (this.$video.paused || this.$video.ended) {
            this.play();
        } else {
            this.pause();
        }
    }

    onToggleSound() {
        this.isMuted ? this.playAudio() : this.muteAudio();
        this.isMuted = !this.isMuted;
    }

    onVideoEnded() {
        this.reset();
        //this.close()
    }

    onVideoClick() {
        // Click & Focus on the toggle button
        // So we can use the space bar to play/pause right after
        this.$toggle.click();
        this.$toggle.focus();
    }

    onActivate() {
        this.el.setAttribute('aria-hidden', false);
    }

    onPostActivate() {
        this.$triggerCta?.setAttribute('aria-expanded', true);

        this.play();
    }

    onDeactivate() {
        this.el.classList.remove(Player.CLASS.ACTIVE);
        document.documentElement.classList.remove(Player.CLASS.GLOBAL_ACTIVE);

        $hasPlayerOpen.set({
            state: false,
            id: null
        });

        this.el.setAttribute('aria-hidden', true);

        this.pause();
    }

    onPostDeactivate() {
        this.$triggerCta?.setAttribute('aria-expanded', false);
    }

    onPlayerOpenChange(hasPlayerOpen) {
        if (hasPlayerOpen.state && hasPlayerOpen.id === this.playerId) {
            this.el.classList.add(Player.CLASS.ACTIVE);
            document.documentElement.classList.add(Player.CLASS.GLOBAL_ACTIVE);

            requestAnimationFrame(() => {
                this.focusTrap?.activate?.();
            });
        }
    }

    onOpen(e) {
        this.$triggerCta = e?.target;
        const playerId = this.$triggerCta?.dataset?.playerId;
        this.open(playerId);
    }

    onClose() {
        this.close();
    }

    // //////////
    // Methods
    // //////////
    play() {
        this.$video.play();
        !this.isMuted && this.playAudio();
        this.el.classList.add(Player.CLASS.PLAYING);
        this.isPlaying = true;
        this.playRaf();
    }

    pause() {
        this.$video.pause();
        this.el.classList.remove(Player.CLASS.PLAYING);
        this.isPlaying = false;
    }

    playAudio() {
        this.el.classList.remove(Player.CLASS.MUTED);
        this.unMute(this.$video);
    }

    muteAudio() {
        this.el.classList.add(Player.CLASS.MUTED);
        this.mute(this.$video, null);
    }

    updateRaf() {
        // Update default progress value + input value
        this.updateProgress();

        // Lerp progress
        this.progress.smooth += (this.progress.default - this.progress.smooth) * this.lerp;

        // Update progress
        this.updateTimeline();

        // Stop lerp if progress is close enoug to default
        // ===============================================
        if (
            this.lerp != 1 &&
            roundNumber(this.progress.smooth, 3) == roundNumber(this.progress.default, 3)
        ) {
            this.lerp = 1;
        }

        // Add delay before stopping raf (if not playing)
        // ==============================================
        if (
            !this.isPlaying &&
            roundNumber(this.progress.smooth, 3) == roundNumber(this.progress.default, 3)
        ) {
            this.rafIncrement++;

            if (this.rafIncrement >= 30) {
                this.pauseRaf();
            }
        }
    }

    reset() {
        this.el.classList.remove(Player.CLASS.PLAYING);
        this.isPlaying = false;
        this.progress.default = 0;
        this.progress.smooth = 0;
        this.$input.value = 0;
        this.$video.currentTime = 0;
    }

    updateProgress() {
        if (this.$video.duration != null && this.$video.duration > 0) {
            this.progress.default = this.$video.currentTime / this.$video.duration;
        } else {
            this.progress.default = 0;
        }

        // Update input value
        this.$input.value = this.progress.default;
    }

    updateTimeline() {
        this.el.style.setProperty('--progress', this.progress.smooth);
    }

    // RAF
    ///////////////
    playRaf() {
        if (this.isRafPlaying) return;

        this.isRafPlaying = true;
        this.rafIncrement = 0;
        gsap.ticker.add(this.updateRaf);
    }

    pauseRaf() {
        if (!this.isRafPlaying) return;

        this.isRafPlaying = false;
        gsap.ticker.remove(this.updateRaf);
    }

    // Audio
    ///////////////
    unMute(media, fromStart = false, autoplay = false, callback = null) {
        if (this.interval) clearInterval(this.interval);

        if (fromStart) media.currentTime = 0;
        if (autoplay) media.play();

        let volume = 0;
        media.muted = false;
        media.volume = 0;

        this.interval = setInterval(() => {
            if (volume < 1) {
                volume += 0.05;
                media.volume = clamp(0, 1, volume).toFixed(2);
            } else {
                clearInterval(this.interval);
                callback?.();
            }
        }, 50);
    }

    /**
     * Progressive mute media.
     *
     * @param {HTMLMediaElement} media - The media element to play.
     * @return {void}
     */
    mute(media, callback = null) {
        if (this.interval) clearInterval(this.interval);

        let volume = media.volume || 1;
        this.interval = setInterval(() => {
            if (volume > 0) {
                volume -= 0.05;
                media.volume = clamp(0, 1, volume).toFixed(2);
            } else {
                clearInterval(this.interval);
                media.muted = true;
                callback?.();
            }
        }, 50);
    }

    open(playerId) {
        if ($hasPlayerOpen.value.state) return;

        $hasPlayerOpen.set({
            state: true,
            id: playerId
        });
    }

    close() {
        if (!$hasPlayerOpen.value.state) return;
        this.$triggerCta = null;
        this.focusTrap?.deactivate?.();
    }
}
